import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Card, CardContent, Button, TextField, Grid, Typography } from '@material-ui/core';
import { MenuWithId } from 'components';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    marginBottom: theme.spacing(3),
    flexGrow: 1,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40%',
    bgcolor: 'background.white',
    boxShadow: 24,
  },
  modalActionCancel: {
    marginLeft: '2%',
  },
  modalActionPrimary: {
    marginLeft: '5%',
  },
}));

const EventsForm = ({ passedEvent, submitEdit, hide, devices, fields, properties }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  let errors = {};
  const editEvent = !!passedEvent.id;
  const [newEventValues, setNewEvent] = useState({});
  const [selectedValues, setSelectedValues] = useState({
    type: '',
    layer: '',
    layer_id: '',
    data: '',
    time: '',
  });
  const [activeLayer, setSelectedLayer] = useState([]);

  const handleFieldChange = (event, field, value) => {
    if (field === 'layer') {
      modifyActiveLayer(value);
    }

    if (field === 'layer_id') {
      const layer = _.find(activeLayer, { id: value });
      setSelectedValues({ ...selectedValues, [field]: layer.name ? layer.name : layer.hw_address });
      setNewEvent({ ...newEventValues, [field]: value });
    } else {
      setSelectedValues({ ...selectedValues, [field]: value.toString() });
      setNewEvent({ ...newEventValues, [field]: value });
    }
  };

  const modifyActiveLayer = (layer) => {
    switch (layer.toLowerCase()) {
      case 'property':
        setSelectedLayer(properties);
        break;
      case 'field':
        setSelectedLayer(fields);
        break;
      case 'device':
        setSelectedLayer(devices);
        break;
      default:
        setSelectedLayer([]);
    }
  };

  const handleSubmit = () => {
    const wellFormedValues = {
      layer: newEventValues.layer,
      type: newEventValues.type,
      layer_id: newEventValues.layer_id,
      data: newEventValues.data,
      time: newEventValues.time,
    };

    submitEdit(wellFormedValues);
    hide();
  };

  const layerList = _.map(activeLayer, (def) => {
    return { name: def.name ? def.name : def.hw_address, id: def.id };
  });

  return (
    <Card className={classes.modal}>
      <CardContent>
        <form
          onSubmit={() => {
            handleSubmit();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={12}>
              <Typography variant="h3">{editEvent ? t('event_modify') : t('event_create')}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroupGrid}>
                <MenuWithId
                  placeholder="Type"
                  menuItems={[
                    { id: 0, name: 'Info' },
                    { id: 1, name: 'Warning' },
                    { id: 2, name: 'Error' },
                  ]}
                  selectedItem={selectedValues.type}
                  disabled={editEvent}
                  selectItem={(item) => handleFieldChange({}, 'type', item.name)}
                  size="normal"
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroupGrid}>
                <MenuWithId
                  placeholder="Layer"
                  menuItems={[
                    { id: 0, name: 'Property' },
                    { id: 1, name: 'Field' },
                    { id: 2, name: 'Device' },
                  ]}
                  disabled={editEvent}
                  selectedItem={selectedValues.layer}
                  selectItem={(item) => handleFieldChange({}, 'layer', item.name)}
                  size="normal"
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={12}>
              {!!selectedValues.layer && selectedValues.layer !== 'Any' && (
                <div className={classes.formGroupGrid}>
                  <MenuWithId
                    placeholder="Associated Layer Object"
                    menuItems={layerList ?? ['None']}
                    selectedItem={selectedValues.layer_id}
                    selectItem={(item) => handleFieldChange({}, 'layer_id', item.id)}
                    inputProps={{
                      maxLength: 24,
                    }}
                    size="normal"
                  />
                </div>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  label={t('data')}
                  name="data"
                  onChange={(event) => handleFieldChange(event, 'data', event.target.value)}
                  value={selectedValues?.data}
                  placeholder={t('data')}
                  disabled={editEvent}
                  helperText={errors && errors.data}
                  error={!!(errors && errors.data)}
                  required
                  multiline
                  minRows={5}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroupGrid}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePicker
                    label={t('event_date')}
                    name={'time'}
                    placeholder={t('event_date')}
                    value={selectedValues.time}
                    onChange={(event) => handleFieldChange({}, 'time', event)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </div>
            </Grid>
          </Grid>
          <Button onClick={hide} color="primary" className={classes.modalActionCancel}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" className={classes.modalActionPrimary}>
            {t('submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

EventsForm.propTypes = {
  passedEvent: PropTypes.object,
  submitEdit: PropTypes.func,
  hide: PropTypes.func,
  devices: PropTypes.array,
  fields: PropTypes.array,
  properties: PropTypes.array,
};

EventsForm.defaultProps = {
  event: {},
  fields: {},
};

export default EventsForm;
